import PropTypes from 'prop-types'
import PublicNav from './PublicNav'
import Footer from './Footer'

const TermsOfService = ({ onBack, onLogin, onShowFeatures, onShowPricing, onShowAbout, onShowContact, onShowPrivacy }) => {
  return (
    <div className="min-h-screen bg-gray-950">
      <PublicNav 
        onShowFeatures={onShowFeatures}
        onShowPricing={onShowPricing}
        onLogin={onLogin}
        onBack={onBack}
        activeProduct={false}
        activePricing={false}
        activeHome={false}
      />

      <div className="relative py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="font-display text-5xl font-light tracking-tight text-white sm:text-7xl mb-6">
              Terms of <span className="text-sky-300">Service</span>
            </h1>
            <p className="mx-auto max-w-3xl text-xl text-gray-400">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="space-y-8 text-gray-300">
              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing and using Opptora ("the Service"), you accept and agree to be bound by these 
                  Terms of Service. If you do not agree to these terms, please do not use the Service.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">2. Description of Service</h2>
                <p>
                  Opptora is an AI-powered platform that provides idea validation and market research services. 
                  We use artificial intelligence to analyze business ideas and provide insights, recommendations, 
                  and market research data.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">3. User Accounts</h2>
                <p>To use our Service, you must:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate and complete information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Be at least 18 years old</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">4. Intellectual Property</h2>
                <p className="font-semibold text-white">Your Content:</p>
                <p>
                  You retain ownership of any business ideas, concepts, or content you submit to Opptora. 
                  By submitting content, you grant us a license to process and analyze it to provide our services.
                </p>
                <p className="font-semibold text-white mt-4">Our Content:</p>
                <p>
                  The Service, including all AI-generated insights, analysis, and recommendations, is owned by 
                  Levantar AI Limited. You may not copy, modify, or distribute our content without permission.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">5. Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the Service for any illegal purposes</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the Service</li>
                  <li>Submit false, misleading, or harmful content</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Reverse engineer or attempt to extract our AI models</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">6. AI-Generated Content Disclaimer</h2>
                <p>
                  Our Service uses artificial intelligence to generate insights and recommendations. While we strive 
                  for accuracy, AI-generated content may contain errors or inaccuracies. You should:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Independently verify all information</li>
                  <li>Consult with professional advisors before making business decisions</li>
                  <li>Not rely solely on AI-generated content for critical decisions</li>
                </ul>
                <p className="mt-4 font-semibold text-white">
                  We make no guarantees about the accuracy, completeness, or suitability of AI-generated content 
                  for any particular purpose.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">7. Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by law, Levantar AI Limited shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether 
                  incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">8. Beta Service</h2>
                <p>
                  Opptora is currently in private beta. The Service may contain bugs, errors, or incomplete features. 
                  We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">9. Termination</h2>
                <p>
                  We may terminate or suspend your access to the Service immediately, without prior notice or liability, 
                  for any reason, including breach of these Terms. Upon termination, your right to use the Service will 
                  immediately cease.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">10. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. We will notify users of any material changes. 
                  Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">11. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of England and Wales, 
                  without regard to its conflict of law provisions.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl font-light tracking-tight text-white mb-4">12. Contact Information</h2>
                <p>
                  If you have any questions about these Terms, please contact us at:
                </p>
                <p className="text-sky-300 font-semibold">hello@opptora.ai</p>
                <p className="mt-4">
                  <span className="font-semibold text-white">Levantar AI Limited</span><br />
                  London, United Kingdom
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer variant="public" onShowFeatures={onShowFeatures} onShowPricing={onShowPricing} onShowAbout={onShowAbout} onShowContact={onShowContact} onShowPrivacy={onShowPrivacy} />
    </div>
  )
}

TermsOfService.propTypes = {
  onBack: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onShowFeatures: PropTypes.func.isRequired,
  onShowPricing: PropTypes.func.isRequired,
  onShowAbout: PropTypes.func,
  onShowContact: PropTypes.func,
  onShowPrivacy: PropTypes.func,
}

export default TermsOfService
